import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronDown, Clock, Newspaper } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import Markdown from "@/lib/markdown";
import { siteConfig } from "@/config/siteConfig";
import {
  getBySlug,
  getRelated,
  HERO_IMAGE_MAP,
} from "@/data/blogData";

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" });
}

const ArticleCta = () => (
  <section
    className="mt-16 -mx-6 lg:-mx-10 border-t border-[var(--border)] bg-[var(--bg-alt)]"
    data-testid="article-cta"
  >
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-12 lg:py-16 text-center">
      <p className="eyebrow">Next Step</p>
      <h2 className="font-heading mt-3 text-[24px] lg:text-[28px] font-semibold text-[var(--navy)] tracking-tight">
        Have a printing or coding requirement?
      </h2>
      <p className="mt-3 text-[15px] text-[var(--charcoal)] max-w-2xl mx-auto">
        Share your substrate, coder platform and application details. We will respond with the trial approach best suited to your line.
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Link to="/request-sample" className="btn-primary" data-testid="article-cta-sample">
          Request a Sample
        </Link>
        <Link to="/contact" className="btn-outline" data-testid="article-cta-contact">
          Talk to Our Team
        </Link>
      </div>
    </div>
  </section>
);

const FaqBlock = ({ faq }) => {
  const [open, setOpen] = useState(0);
  return (
    <section className="mt-16" data-testid="article-faq-block">
      <h2 className="font-heading text-[26px] lg:text-[30px] font-semibold text-[var(--navy)] tracking-tight">
        Frequently Asked Questions
      </h2>
      <div className="mt-6 space-y-3 max-w-3xl">
        {faq.map((f, i) => {
          const isOpen = open === i;
          return (
            <div
              key={i}
              className="border border-[var(--border)] rounded-[4px] bg-white"
              data-testid={`article-faq-${i}`}
            >
              <button
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                className="w-full text-left flex items-start justify-between gap-4 p-5"
                data-testid={`article-faq-toggle-${i}`}
              >
                <span className="font-heading text-[16.5px] font-semibold text-[var(--navy)]">
                  {f.q}
                </span>
                <ChevronDown
                  className={`w-5 h-5 shrink-0 mt-0.5 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  style={{ color: "var(--pharma-blue)" }}
                  aria-hidden="true"
                />
              </button>
              {isOpen && (
                <div className="px-5 pb-5 -mt-1 text-[15px] leading-relaxed text-[var(--charcoal)]">
                  {f.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const RelatedGrid = ({ related }) => {
  if (!related.length) return null;
  return (
    <section className="mt-16" data-testid="article-related">
      <p className="eyebrow">Related Articles</p>
      <h2 className="font-heading mt-3 text-[24px] lg:text-[28px] font-semibold text-[var(--navy)] tracking-tight">
        Continue reading
      </h2>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
        {related.map((p) => (
          <article
            key={p.slug}
            className="border border-[var(--border)] rounded-[4px] p-5 bg-white flex flex-col hover:border-[var(--navy)] transition-colors"
            data-testid={`related-card-${p.slug}`}
          >
            <p className="eyebrow" style={{ color: "var(--pharma-blue)" }}>{p.category}</p>
            <h3 className="font-heading mt-3 text-[17px] leading-snug font-semibold text-[var(--navy)]">
              <Link to={`/blog/${p.slug}`} className="hover:underline underline-offset-4" data-testid={`related-link-${p.slug}`}>
                {p.title}
              </Link>
            </h3>
            <p className="mt-2 text-[13.5px] leading-relaxed text-[var(--slate)] line-clamp-2 flex-1">
              {p.excerpt}
            </p>
            <Link
              to={`/blog/${p.slug}`}
              className="arrow-link mt-4 text-[13px]"
              data-testid={`related-arrow-${p.slug}`}
            >
              Read article →
            </Link>
          </article>
        ))}
      </div>
    </section>
  );
};

const BlogArticle = () => {
  const { slug } = useParams();
  const post = getBySlug(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const path = `/blog/${post.slug}`;
  const canonical = `${siteConfig.DOMAIN}${path}`;
  const heroSrc = HERO_IMAGE_MAP[post.heroImageKey] || HERO_IMAGE_MAP.sample;
  const ogImage = `${siteConfig.DOMAIN}${heroSrc}`;
  const isStub = post.status === "stub";

  const related = isStub ? [] : getRelated(post, 3);

  // ---------- JSON-LD ----------
  const wordCount = post.body
    ? post.body.replace(/[`#*_>[\](){}]/g, " ").split(/\s+/).filter(Boolean).length
    : undefined;
  const articleSchema = (() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      image: [ogImage],
      author: { "@type": "Organization", name: siteConfig.COMPANY_NAME, url: siteConfig.DOMAIN },
      publisher: {
        "@type": "Organization",
        name: siteConfig.COMPANY_NAME,
        url: siteConfig.DOMAIN,
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      articleSection: post.category,
    };
    if (post.publishedDate) {
      schema.datePublished = post.publishedDate;
      schema.dateModified = post.publishedDate;
    }
    if (wordCount) schema.wordCount = wordCount;
    return schema;
  })();

  const faqSchema = post.faq && post.faq.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <>
      <SEO
        rawTitle={post.seoTitle}
        description={post.seoDescription}
        path={path}
        ogImage={ogImage}
        ogType="article"
        robotsContent={isStub ? "noindex,follow" : null}
      />
      <Helmet>
        {post.publishedDate && (
          <meta property="article:published_time" content={post.publishedDate} />
        )}
        <meta property="article:section" content={post.category} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Helmet>

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Knowledge Centre", to: "/blog" },
            {
              label: post.category,
              to: `/blog?category=${post.category.toLowerCase().replace(/\s*&\s*/g, "-").replace(/\s+/g, "-")}`,
            },
            { label: post.title },
          ]}
        />
      </div>

      {/* Header */}
      <header
        className="mx-auto max-w-[840px] px-6 lg:px-10 pt-10 lg:pt-14 pb-8 text-center"
        data-testid="article-header"
      >
        <p className="eyebrow" style={{ color: "var(--pharma-blue)" }}>{post.category}</p>
        <h1
          className="font-heading mt-4 text-[34px] leading-[1.1] lg:text-[48px] lg:leading-[1.05] font-semibold text-[var(--navy)] tracking-tight mx-auto max-w-3xl"
          data-testid="article-h1"
        >
          {post.title}
        </h1>
        <div className="mt-5 text-[13px] text-[var(--slate)] flex items-center justify-center gap-2 flex-wrap" data-testid="article-meta">
          {isStub ? (
            <span className="uppercase tracking-wider">Coming soon</span>
          ) : (
            <>
              <span>Published {fmtDate(post.publishedDate)}</span>
              <span aria-hidden="true">·</span>
              <span className="inline-flex items-center gap-1"><Clock size={12} strokeWidth={1.6} />{post.readingTimeMin} min read</span>
            </>
          )}
        </div>
        <div className="mt-8 border-t border-[var(--border)]" />
      </header>

      {/* Body */}
      <section
        className="mx-auto max-w-[840px] px-6 lg:px-10 pb-4"
        data-testid={isStub ? "article-stub-body" : "article-body"}
      >
        {isStub ? (
          <div className="border border-[var(--border)] rounded-[4px] bg-[var(--bg-alt)] p-8 lg:p-10 text-center">
            <Newspaper className="mx-auto mb-3 text-[var(--pharma-blue)]" size={28} strokeWidth={1.5} />
            <p className="text-[16px] leading-relaxed text-[var(--charcoal)] max-w-xl mx-auto">
              This article is being prepared. In the meantime, contact us to discuss your application — we can share the technical documentation and trial approach relevant to your line.
            </p>
          </div>
        ) : (
          <div className="max-w-[720px] mx-auto">
            <Markdown>{post.body}</Markdown>
          </div>
        )}
      </section>

      {/* FAQ */}
      {!isStub && post.faq && (
        <section className="mx-auto max-w-[840px] px-6 lg:px-10">
          <div className="max-w-[720px] mx-auto">
            <FaqBlock faq={post.faq} />
          </div>
        </section>
      )}

      {/* Related */}
      {!isStub && (
        <section className="mx-auto max-w-[1240px] px-6 lg:px-10">
          <RelatedGrid related={related} />
        </section>
      )}

      {/* CTA */}
      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 mt-6">
        <ArticleCta />
      </div>
    </>
  );
};

export default BlogArticle;
